<script setup lang="ts">
// Barra de cima do jogo em fullscreen (mobile). Desenho: Figma node
// 20505:52103 — frame de 393 × 52, com uma linha de 34 e padding 9/10.
// Layout: [‹ Voltar] · [logo] · [Sacar][Depositar].
const { openDepositModal, openWithdrawModal } = usePayments();
const { userCanOnlyWithdraw } = useUserLimits();
const { isAuthenticated } = storeToRefs(useAuthStore());
const casinoStore = useCasinoStore();
const { updateWallet } = useWallet();
const { lastPreviewGame, reopenLastPreview } = useGameMobilePreview();
const router = useRouter();

// Lockup EMPILHADO (ícone + "APOSTA" / "TUDO"), que é o que o Figma usa aqui.
// Não dá pra reusar o BaseCommonLogo: ele serve o logo horizontal (proporção
// 6,8), que na altura desta barra daria ~164px de largura e comeria o espaço
// dos botões. O arquivo está aparado no conteúdo opaco, então a altura abaixo
// é a do logo VISÍVEL — não a da caixa com respiro do Figma.
const logoUrl = computed(() =>
  getAssetsUrlForCDN('/assets/images/logo-vertical-white.webp'),
);

type Emits = {
  'full-screen-off': [];
};

const emit = defineEmits<Emits>();

function backHome() {
  emit('full-screen-off');
  casinoStore.isGameModalOpen = false;
  updateWallet();

  // Veio do MobilePreview: volta pra rota anterior e reabre o preview
  // no jogo que estava sendo visualizado. Quando não há histórico (entrada
  // direta via deeplink ?direct=true), navega pra home pra que o preview
  // tenha onde renderizar como overlay sobre o conteúdo principal.
  if (lastPreviewGame.value) {
    const hasHistory = !!window.history.state?.back;

    if (hasHistory) {
      router.back();
    } else {
      router.push('/');
    }

    reopenLastPreview();
  }
}

onBeforeRouteLeave(() => {
  casinoStore.isGameModalOpen = false;
});
</script>

<template>
  <div
    class="gamesBar"
    :class="{ hidden: !casinoStore.isGameModalOpen }"
  >
    <div class="menu">
      <button
        class="back"
        type="button"
        @click="backHome"
      >
        <NuxtIcon
          class="backIcon"
          name="arrows/chevron_left"
        />
        {{ $t('casino.game.go_back') }}
      </button>

      <!-- O logo é um item normal do `space-between`, como no Figma — ou seja,
           NÃO está no centro exato da barra: os dois grupos laterais têm
           larguras diferentes, então ele cai ~18px à esquerda do centro. -->
      <NuxtLink
        :aria-label="appName()"
        class="logo"
        no-prefetch
        :to="localeLink('/')"
      >
        <img
          :alt="appName()"
          :src="logoUrl"
        />
      </NuxtLink>

      <div class="actions">
        <!-- Saque não existe pra quem não está logado (o openWithdrawModal
             retorna sem fazer nada), então o botão nem entra. -->
        <button
          v-if="isAuthenticated"
          class="btn-withdraw"
          type="button"
          @click="openWithdrawModal()"
        >
          {{ $t('account.wallet.withdraw') }}
        </button>

        <!-- Mesma regra do header do site (components/user/header/Buttons.vue):
             quem só pode sacar não vê depósito. -->
        <button
          v-if="!userCanOnlyWithdraw"
          class="btn-deposit"
          type="button"
          @click="openDepositModal()"
        >
          {{ $t('header.deposit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gamesBar {
  @apply bg-bg-secondary w-full fixed top-0 left-0 z-[99999];

  // Publicada em :root (main.scss) porque o GameIframe desconta esta altura do
  // iframe — as duas pontas leem o mesmo valor.
  height: var(--games-bar-h);
  // 9 na vertical e 10 na horizontal (Figma): 9 + 34 da linha + 9 = 52.
  padding: 9px 10px;
  // Sem borda embaixo: o Figma fecha a barra chapada contra o jogo.
}

.menu {
  @apply flex flex-row items-center justify-between w-full;

  height: 34px;
}

/* ---------- voltar ---------- */
.back {
  @apply flex items-center shrink-0 text-white;

  // 8,55 no Figma, medido da ponta do chevron até o texto.
  gap: 8.5px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  -webkit-tap-highlight-color: transparent;
}

// `chevron_left` e não o `angle_left` que estava aqui: o desenho pede um chevron
// FINO de 7,33 × 12,22 (proporção 0,60), e é essa a proporção do viewBox do
// chevron (17 × 28) — o angle_left é atarracado (0,90). O ícone é `1em`, com o
// traço limitado pela ALTURA, então `font-size` = a altura desejada.
.backIcon {
  @apply inline-flex items-center;

  font-size: 12.2px;

  :deep(svg) {
    // O arquivo declara `width: 1em`, mas o traço só ocupa 17/28 = 0,607em da
    // caixa e fica centrado nela — sobrava ~2,4px de vazio de cada lado, que
    // empurravam o chevron pra dentro e inflavam o gap até "Voltar". Com a
    // largura na proporção do viewBox a caixa abraça o traço.
    width: 0.607em;
    // O NuxtIcon injeta um <svg> com margin-bottom próprio; zera pra alinhar o
    // chevron com a linha do texto.
    margin-bottom: 0;
  }
}

/* ---------- logo ---------- */
.logo {
  @apply flex items-center shrink-0;

  img {
    @apply block w-auto;

    // Altura do logo VISÍVEL no desenho: a caixa do Figma é 83,8 × 23,9, mas o
    // conteúdo opaco dentro dela mede 68,5 × 17,37.
    height: 17.37px;
  }
}

/* ---------- sacar / depositar ---------- */
.actions {
  // `lg:hidden` mantém o que já valia aqui: no desktop (onde o fullscreen é
  // caminho de exceção e o header do site continua à mão) a barra fica só com
  // voltar + logo.
  @apply flex items-center shrink-0 lg:hidden;
}

// Os dois são o MESMO botão do DS, mudando só o fundo. As medidas saem da
// instância do Figma, que está reduzida a 64,18% do botão padrão: altura 25,67,
// raio 6,42, padding lateral 9,63.
.btn-withdraw,
.btn-deposit {
  @apply flex items-center justify-center whitespace-nowrap;

  height: 25.67px;
  min-width: 25.67px;
  padding: 0 9.63px;
  border-radius: 6.42px;
  color: #fcfcfc;
  // 10px, e NÃO os 7,72 da instância do Figma: 7,72 é resultado da instância
  // inteira ter sido escalada a 64%, e texto abaixo de ~10px não se lê num
  // botão. 10 é o menor tamanho de rótulo que o resto do app usa.
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
}

// Fica ATRÁS do depositar, com 10,43 de sobreposição — no desenho os dois pills
// se encaixam, e o "Sacar" some por baixo do laranja.
.btn-withdraw {
  background: rgba(13, 29, 68, 0.5); // default/default-soft

  margin-right: -10.43px;
  // A sobreposição entra também no padding da direita, senão o rótulo (que se
  // centra na caixa INTEIRA do botão) cai debaixo do laranja e o "r" de "Sacar"
  // fica cortado. Com ela aqui, "Sacar" se centra no pedaço VISÍVEL do pill.
  padding-right: 20.06px; // 9,63 do botão + 10,43 de sobreposição
}

.btn-deposit {
  // `relative` sem z-index basta pra pintar por cima do irmão anterior.
  @apply relative;

  background: #ff3700; // accent/accent
}

.btn-withdraw:active,
.btn-deposit:active {
  filter: brightness(0.94);
}
</style>
